import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Editor, Element as SlateElement, Range, Transforms, Path } from "slate";
import { useSlate, ReactEditor } from "slate-react";
import { v4 as uuidv4 } from "uuid";
import { ELEMENT_LINK } from "./Post/platePlugins";
import "./LinkButton.css";

const normalizeUrl = (rawUrl) => {
  const trimmed = rawUrl.trim();
  if (!trimmed) return "";

  const hasScheme = /^([a-z][a-z0-9+.-]*):\/\//i.test(trimmed);
  const candidate = hasScheme ? trimmed : `https://${trimmed}`;

  try {
    const url = new URL(candidate);
    if (url.protocol === "http:" || url.protocol === "https:") {
      return url.toString();
    }
    return "";
  } catch (e) {
    return "";
  }
};

const LinkButton = ({ className = "toolbar-button", disabled = false }) => {
  const editor = useSlate();
  const [isOpen, setIsOpen] = useState(false);
  const [linkData, setLinkData] = useState({ name: "", url: "" });
  const [error, setError] = useState("");

  const findActiveLinkEntry = useCallback(() => {
    if (!editor.selection) return null;
    const [entry] = Editor.nodes(editor, {
      at: editor.selection,
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n.type === ELEMENT_LINK,
      mode: "lowest",
    });
    return entry || null;
  }, [editor]);

  /**
   * 打開插入連結對話框
   */
  const openLinkDialog = () => {
    ReactEditor.focus(editor);

    let initialName = "";
    let initialUrl = "";

    const activeLinkEntry = findActiveLinkEntry();

    if (activeLinkEntry) {
      const [node, path] = activeLinkEntry;
      initialUrl = node?.url || "";
      try {
        initialName = Editor.string(editor, Editor.range(editor, path)) || "";
      } catch (e) {
        initialName = "";
      }
    } else if (editor.selection && !Range.isCollapsed(editor.selection)) {
      initialName = Editor.string(editor, editor.selection) || "";
    }

    setLinkData({
      name: initialName.trim(),
      url: initialUrl,
    });
    setError("");
    setIsOpen(true);
  };

  /**
   * 處理輸入變更
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLinkData({ ...linkData, [name]: value });
    if (error) {
      setError("");
    }
  };

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setError("");
    setLinkData({ name: "", url: "" });
  }, []);

  const replaceLinkText = (path, text) => {
    if (!text) return;

    const range = Editor.range(editor, path);
    Transforms.select(editor, range);
    Transforms.insertText(editor, text);
  };

  const insertLinkNode = (url, text) => {
    const linkNode = {
      type: ELEMENT_LINK,
      url,
      id: uuidv4(),
      children: [{ text: text || url }],
    };

    Transforms.insertNodes(editor, linkNode);
  };

  const moveSelectionAfterLink = useCallback(() => {
    const activeEntry = findActiveLinkEntry();
    if (!activeEntry) return;

    const [, path] = activeEntry;
    let afterPoint = Editor.after(editor, path);

    if (!afterPoint) {
      const insertPath = Path.next(path);
      Transforms.insertNodes(editor, { text: "" }, { at: insertPath });
      afterPoint = Editor.after(editor, path);
    }

    if (afterPoint) {
      Transforms.select(editor, afterPoint);
    }
  }, [editor, findActiveLinkEntry]);

  /**
   * 插入連結
   */
  const insertLink = useCallback(() => {
    const name = linkData.name.trim();
    const normalizedUrl = normalizeUrl(linkData.url);

    if (!normalizedUrl) {
      setError("請輸入有效的網址，需包含 http(s) 協定");
      return;
    }

    ReactEditor.focus(editor);

    const activeLinkEntry = findActiveLinkEntry();

    if (activeLinkEntry) {
      const [, path] = activeLinkEntry;
      Transforms.setNodes(
        editor,
        { url: normalizedUrl },
        { at: path }
      );
      if (name) {
        replaceLinkText(path, name);
      }
    } else if (editor.selection && !Range.isCollapsed(editor.selection)) {
      Transforms.unwrapNodes(editor, {
        at: editor.selection,
        match: (n) =>
          !Editor.isEditor(n) &&
          SlateElement.isElement(n) &&
          n.type === ELEMENT_LINK,
        split: true,
      });

      if (name) {
        Transforms.delete(editor);
        insertLinkNode(normalizedUrl, name);
      } else {
        const wrapper = {
          type: ELEMENT_LINK,
          url: normalizedUrl,
          id: uuidv4(),
          children: [],
        };
        Transforms.wrapNodes(editor, wrapper, { split: true });
        Transforms.collapse(editor, { edge: "end" });
      }
    } else {
      if (editor.selection) {
        Transforms.unwrapNodes(editor, {
          at: editor.selection,
          match: (n) =>
            !Editor.isEditor(n) &&
            SlateElement.isElement(n) &&
            n.type === ELEMENT_LINK,
          split: true,
        });
      }
      const text = name || normalizedUrl;
      insertLinkNode(normalizedUrl, text);
    }

    Transforms.collapse(editor, { edge: "end" });
    moveSelectionAfterLink();
    closeDialog();
  }, [closeDialog, editor, findActiveLinkEntry, linkData.name, linkData.url, moveSelectionAfterLink]);

  /**
   * 渲染連結輸入框
   */
  const renderLinkDialog = () => {
    const trimmedUrl = linkData.url.trim();
    const previewUrl = trimmedUrl ? normalizeUrl(trimmedUrl) : "";

    const dialog = (
      <div
        className="link-modal-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            closeDialog();
          }
        }}
      >
        <div className="link-modal" role="dialog" aria-modal="true" aria-labelledby="link-modal-title">
          <div className="link-modal__header">
            <h3 id="link-modal-title">新增連結</h3>
            <button  className="link-modal__close" 
            onClick={closeDialog}
            >
              ×
            </button>
          </div>
          <div className="link-modal__body">
            <label className="link-field">
              <span className="link-field__label">顯示文字</span>
              <input
                type="text"
                name="name"
                placeholder="連結名稱（可選）"
                value={linkData.name}
                onChange={handleInputChange}
                className="link-field__input"
              />
            </label>
            <label className="link-field">
              <span className="link-field__label">連結網址</span>
              <input
                type="url"
                name="url"
                placeholder="https://example.com"
                value={linkData.url}
                onChange={handleInputChange}
                className={`link-field__input ${error ? "link-field__input--error" : ""}`}
                autoFocus
                inputMode="url"
              />
            </label>
            {error && <p className="link-modal__error">{error}</p>}
            {trimmedUrl && !error && previewUrl && (
              <a
                className="link-preview"
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                預覽：{previewUrl}
              </a>
            )}
          </div>
          <div className="link-modal__footer">
            <button onClick={closeDialog} className="link-btn secondary" >
              取消
            </button>
            <button onClick={insertLink} className="link-btn primary" >
              確認插入
            </button>
          </div>
        </div>
      </div>
    );

    if (typeof document === "undefined") {
      return dialog;
    }

    return createPortal(dialog, document.body);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeDialog();
      }
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        insertLink();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeDialog, insertLink, isOpen]);

  return (
    <>
      <button
        type="button"
        onMouseDown={(e) => {
          if (disabled) return;
          e.preventDefault();
          openLinkDialog();
        }}
        className={className}
        aria-label="插入連結"
        disabled={disabled}
      >
        🔗 插入連結
      </button>
      {isOpen && renderLinkDialog()}
    </>
  );
};

export default LinkButton;
